import { useState } from "react";
import {
  ListItem,
  ListItemAvatar,
  ListItemText,
  Typography,
  MenuItem,
  Tooltip,
  IconButton,
  Popover,
  ListItemIcon,
} from "@mui/material";
import CopyLinkButton from "./CopyLinkButton";
import AvatarImage from "./AvatarImage";
import { IMAGE_STATE } from "types/imageState";
import { useFilter } from "hooks/filter";
import { useMediaModal } from "hooks/mediaModal";
import { Media } from "types/qbank/media";
import { getPublishUrl } from "utilities/mediaUrl";
import { DeploymentSite } from "types/qbank/deploymentSite";
import AspectRatioIcon from "@mui/icons-material/AspectRatio";
import Check from "@mui/icons-material/Check";

type IProps = {
  media: Media;
  deploymentSite: DeploymentSite;
};

const MediaList = ({ media, deploymentSite }: IProps) => {
  const { searchState } = useFilter();
  const { setModal } = useMediaModal();
  const [templateId, setTemplateId] = useState<number>(
    parseInt(searchState.currentCategory.imageTemplateId)
  );
  const publishUrl = getPublishUrl(deploymentSite, media.deployedFiles, templateId);
  const availableTemplates = media.deployedFiles?.filter(
    (deployedFile) =>
      deployedFile.imageTemplateId !== null &&
      deployedFile.deploymentSiteId === deploymentSite.id
  );
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;

  return (
    <ListItem
      key={media.mediaId}
      disablePadding
      disableGutters
      divider
      alignItems="flex-start"
      secondaryAction={
        <>
          {availableTemplates && availableTemplates.length > 0 && (
            <>
              <Typography
                variant="caption"
                color="text.primary"
                noWrap={true}
                textOverflow="ellipsis"
                sx={{ mr: 1 }}
              >
                {
                  availableTemplates.find(
                    (template) => template.imageTemplateId === templateId
                  )?.templateName
                }
              </Typography>
              <Tooltip title="change template">
                <IconButton aria-label="change template" onClick={handleClick}>
                  <AspectRatioIcon />
                </IconButton>
              </Tooltip>
              <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "left",
                }}
              >
                {availableTemplates.map((deployedFile, i) => {
                  const active = templateId === deployedFile.imageTemplateId;
                  return (
                    <MenuItem
                      dense
                      divider
                      key={i}
                      value={deployedFile.imageTemplateId || 1}
                      onClick={() => {
                        setTemplateId(deployedFile.imageTemplateId || 1);
                        handleClose();
                      }}
                    >
                      {active && (
                        <ListItemIcon>
                          <Check />
                        </ListItemIcon>
                      )}
                      <ListItemText inset={active ? false : true}>
                        {deployedFile.templateName ||
                          JSON.parse(deployedFile.metadata)["templateName"]}
                      </ListItemText>
                    </MenuItem>
                  );
                })}
              </Popover>
            </>
          )}
          <CopyLinkButton
            media={media}
            text={publishUrl || ""}
            imageState={IMAGE_STATE.PRIMARY}
            disabled={false}
          />
        </>
      }
    >
      <ListItemAvatar onClick={() => setModal({ open: true, media })}>
          <AvatarImage alt={`${media.filename}`} src={publishUrl || ""} />
      </ListItemAvatar>
      <ListItemText
        inset={false}
        primary={media.name}
        secondary={
          <Typography
            sx={{ display: "inline" }}
            component="span"
            variant="body2"
            noWrap
            color="text.primary"
          >
            {media.mediaId}
          </Typography>
        }
      />
    </ListItem>
  );
};

export default MediaList;
